import { useState, useRef, useEffect, useCallback, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { CheckIcon, ChevronsUpDownIcon, SearchIcon, XIcon } from "lucide-react"
import { cn } from "@/lib/utils"

export interface SearchableOption {
  value: string
  label: string
  sublabel?: string | undefined  // additional search-only text
  keywords?: string | undefined  // additional search-only text
  disabled?: boolean
  startIcon?: ReactNode          // optional icon/avatar rendered before the label
}

export function matchesSearchableOption(option: SearchableOption, search: string): boolean {
  const term = search.toLowerCase()
  return (
    option.label.toLowerCase().includes(term) ||
    (option.sublabel ?? "").toLowerCase().includes(term) ||
    (option.keywords ?? "").toLowerCase().includes(term)
  )
}

// ─── Single-select ────────────────────────────────────────────────────────────

interface SingleProps {
  multiple?: false
  options: SearchableOption[]
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyLabel?: string
  disabled?: boolean
  /** When provided, disables client-side filtering and calls this instead (server-side search) */
  onSearchChange?: (value: string) => void
  /** Compact mode: h-7 + text-xs + transparent bg, matching the filter bar style */
  compact?: boolean
  /** Value that means "no selection" — shown as placeholder (muted) instead of as a selected item */
  emptyValue?: string
}

// ─── Multi-select ─────────────────────────────────────────────────────────────

interface MultiProps {
  multiple: true
  options: SearchableOption[]
  value: string[]
  onValueChange: (value: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyLabel?: string
  disabled?: boolean
  onSearchChange?: never
  compact?: boolean
}

type Props = SingleProps | MultiProps

// ─── Component ───────────────────────────────────────────────────────────────

export function SearchableSelect(props: Props) {
  const {
    options,
    placeholder = "Seleccionar...",
    searchPlaceholder = "Buscar...",
    emptyLabel = "Sin resultados",
    disabled = false,
    compact = false,
    multiple,
  } = props

  const emptyValue = !multiple ? (props as SingleProps).emptyValue : undefined

  const [open, setOpen]       = useState(false)
  const [search, setSearch]   = useState("")
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 })
  const triggerRef            = useRef<HTMLButtonElement>(null)
  const inputRef              = useRef<HTMLInputElement>(null)

  const calcPos = useCallback(() => {
    if (!triggerRef.current) return
    const r = triggerRef.current.getBoundingClientRect()
    setDropPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 180) })
  }, [])

  useEffect(() => {
    if (!open) return
    calcPos()

    function handleClick(e: MouseEvent) {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      const portal = document.getElementById("searchable-select-portal-active")
      if (portal?.contains(target)) return
      setOpen(false)
      setSearch("")
    }

    document.addEventListener("mousedown", handleClick)
    window.addEventListener("scroll",  calcPos, true)
    window.addEventListener("resize",  calcPos)
    return () => {
      document.removeEventListener("mousedown", handleClick)
      window.removeEventListener("scroll",  calcPos, true)
      window.removeEventListener("resize",  calcPos)
    }
  }, [open, calcPos])

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 10)
  }, [open])

  const onSearchChange = !multiple ? (props as SingleProps).onSearchChange : undefined
  const filtered = onSearchChange
    ? options
    : options.filter(option => matchesSearchableOption(option, search))

  // ── Selection helpers ──────────────────────────────────────────────────────

  function isSelected(val: string) {
    return multiple
      ? (props.value as string[]).includes(val)
      : props.value === val
  }

  function handleSelect(val: string) {
    if (multiple) {
      const current = props.value as string[]
      props.onValueChange(
        current.includes(val) ? current.filter(v => v !== val) : [...current, val]
      )
    } else {
      ;(props as SingleProps).onValueChange(val)
      setOpen(false)
      setSearch("")
    }
  }

  function handleRemoveChip(val: string, e: React.MouseEvent) {
    e.stopPropagation()
    if (multiple) {
      props.onValueChange((props.value as string[]).filter(v => v !== val))
    }
  }

  // ── Trigger label ──────────────────────────────────────────────────────────

  const singleSelected = !multiple ? options.find(o => o.value === props.value) : null
  const multiCount     = multiple  ? (props.value as string[]).length : 0
  // Treat emptyValue (or missing selection) as "no selection" — render as muted placeholder
  const currentValue     = !multiple ? (props.value as string) : ""
  const isEmptySelection = !multiple && (
    emptyValue !== undefined ? currentValue === emptyValue : !singleSelected
  )

  function renderTriggerContent() {
    if (!multiple) {
      return (
        <span className="flex items-center gap-2 truncate min-w-0">
          {!isEmptySelection && singleSelected?.startIcon && <span className="shrink-0">{singleSelected.startIcon}</span>}
          <span className="flex min-w-0 items-baseline gap-1.5 truncate">
            <span className={cn("min-w-0 truncate", isEmptySelection ? "text-muted-foreground" : "text-foreground")}>
              {isEmptySelection ? placeholder : (singleSelected?.label ?? placeholder)}
            </span>
            {!isEmptySelection && singleSelected?.sublabel && (
              <span className="min-w-0 shrink truncate text-xs text-muted-foreground">{singleSelected.sublabel}</span>
            )}
          </span>
        </span>
      )
    }

    if (multiCount === 0) {
      return <span className="text-muted-foreground truncate">{placeholder}</span>
    }

    if (multiCount === 1) {
      const firstVal = (props.value as string[])[0]
      const label = options.find(o => o.value === firstVal)?.label ?? firstVal
      return (
        <span className="flex items-center gap-1 min-w-0">
          <span className="truncate">{label}</span>
          <span
            role="button"
            tabIndex={-1}
            onMouseDown={e => { if (firstVal) handleRemoveChip(firstVal, e) }}
            className="shrink-0 text-muted-foreground hover:text-foreground"
          >
            <XIcon className="size-3" />
          </span>
        </span>
      )
    }

    return (
      <span className="flex items-center gap-1.5">
        <span className="inline-flex items-center justify-center size-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold shrink-0">
          {multiCount}
        </span>
        <span className="text-muted-foreground truncate">seleccionados</span>
      </span>
    )
  }

  // ── Dropdown ───────────────────────────────────────────────────────────────

  const dropdown = open ? (
    <div
      id="searchable-select-portal-active"
      style={{ position: "fixed", top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 9999 }}
      className="rounded-md border bg-popover text-popover-foreground shadow-md"
    >
      {/* Search input */}
      <div className="flex items-center border-b px-3">
        <SearchIcon className="size-3.5 shrink-0 text-muted-foreground mr-2" />
        <input
          ref={inputRef}
          value={search}
          onChange={e => {
            setSearch(e.target.value)
            onSearchChange?.(e.target.value)
          }}
          placeholder={searchPlaceholder}
          className="flex h-9 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>

      {/* Options list */}
      <div className="max-h-52 overflow-y-auto py-1">
        {filtered.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">{emptyLabel}</div>
        ) : (
          filtered.map(o => {
            const checked = isSelected(o.value)
            return (
              <button
                key={o.value}
                type="button"
                onMouseDown={e => {
                  e.preventDefault()
                  if (!o.disabled) handleSelect(o.value)
                }}
                disabled={o.disabled}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-1.5 text-sm",
                  o.disabled
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-default hover:bg-accent hover:text-accent-foreground",
                  !multiple && checked && "bg-accent/50"
                )}
              >
                {multiple ? (
                  <span className={cn(
                    "flex size-3.5 shrink-0 items-center justify-center rounded border border-input",
                    checked && "bg-primary border-primary"
                  )}>
                    {checked && <CheckIcon className="size-2.5 text-primary-foreground" />}
                  </span>
                ) : (
                  <CheckIcon className={cn("size-3.5 shrink-0 text-primary", checked ? "opacity-100" : "opacity-0")} />
                )}
                {o.startIcon && <span className="shrink-0">{o.startIcon}</span>}
                <span className="flex-1 text-left leading-tight min-w-0">
                  <span className="block truncate">{o.label}</span>
                  {o.sublabel && (
                    <span className={cn("block truncate text-xs", o.disabled ? "text-amber-500" : "text-muted-foreground")}>
                      {o.sublabel}
                    </span>
                  )}
                </span>
              </button>
            )
          })
        )}
      </div>

      {/* Footer: clear selection (single with emptyValue, or multi) */}
      {(multiple && multiCount > 0) || (!multiple && !isEmptySelection && emptyValue !== undefined) ? (
        <div className="border-t px-3 py-1.5">
          <button
            type="button"
            onMouseDown={e => {
              e.preventDefault()
              if (multiple) {
                props.onValueChange([])
              } else {
                ;(props as SingleProps).onValueChange(emptyValue!)
              }
            }}
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
          >
            Limpiar selección
          </button>
        </div>
      ) : null}
    </div>
  ) : null

  // ── Trigger ────────────────────────────────────────────────────────────────

  const triggerBase = "flex w-full items-center justify-between border border-input transition-colors outline-none disabled:cursor-not-allowed disabled:opacity-50 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
  const triggerSize = compact
    ? "h-7 pl-2.5 pr-2 gap-1.5 text-xs rounded-[min(var(--radius-md),10px)] bg-transparent dark:bg-input/30 dark:hover:bg-input/50"
    : "h-9 px-3 py-2 text-sm rounded-md bg-background"

  return (
    <div className="relative w-full min-w-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => { if (disabled) return; calcPos(); setOpen(v => !v); setSearch("") }}
        disabled={disabled}
        className={cn(triggerBase, "min-w-0", triggerSize, open && "ring-2 ring-ring ring-offset-2")}
      >
        <span className="flex items-center gap-1 truncate min-w-0 flex-1">
          {renderTriggerContent()}
        </span>
        <ChevronsUpDownIcon className={cn("shrink-0 opacity-50", compact ? "size-3.5 ml-1" : "size-4 ml-2")} />
      </button>

      {typeof document !== "undefined" && createPortal(dropdown, document.body)}
    </div>
  )
}
